/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import {
  Component,
  Input,
  OnInit,
  ViewChild,
  ElementRef,
  Self,
  Optional,
  Renderer2,
  AfterViewInit,
  EventEmitter,
  Output,
} from '@angular/core';
import {
  ControlValueAccessor,
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
  Validators,
  NgControl,
  NgForm,
  NgModel,
} from '@angular/forms';
import { XdaStringsService } from '../../services/xda-strings.service';

@Component({
  selector: 'xrx-input-group',
  templateUrl: './xrx-input-group.component.html',
  styleUrls: ['./xrx-input-group.component.less'],
  host: {
    '(document:submit)': 'onSubmit($event)',
  },
  standalone: false,
})
export class XrxInputGroupComponent
  implements OnInit, AfterViewInit, ControlValueAccessor
{
  @ViewChild('input', { static: false }) input: ElementRef;
  @ViewChild('buttonPrepend', { static: false }) buttonPrepend: ElementRef;
  @ViewChild('buttonAppend', { static: false }) buttonAppend: ElementRef;

  @Input() type: string;
  @Input() id: string;
  @Input() name: string;
  @Input() label: string;
  @Input() pattern: string | RegExp;
  @Input() placeholder: string;
  @Input() error: string;
  @Input() patternerror: string;
  @Input() compareto: string;
  @Input() matcherror: string;
  @Input() minValue: number;
  @Input() maxValue: number;
  @Input() minlength: number;
  @Input() minlengtherror: string;
  @Input() maxlength: number;
  @Input() maxlengtherror: string;
  @Input() required: boolean;
  @Input() autofocus: boolean;
  @Input() ishalf: boolean;
  @Input() isMfd: boolean;
  @Input() viewPassword: boolean;
  @Input() classes: string;
  @Input() noValidateError: string;
  @Input() helpBlock: string;
  @Input() readonly: boolean;
  @Input() autocomplete: string;
  @Input() prependGlyph: string;
  @Input() prependText: string;
  @Input() prependButton: any;
  @Input() appendGlyph: string;
  @Input() appendText: string;
  @Input() appendButton: any;

  @Output() isValid = new EventEmitter<boolean>();
  @Output() inputFocused = new EventEmitter<boolean>();
  @Output() inputBlurred = new EventEmitter<boolean>();
  @Output() inputClicked = new EventEmitter<any>();
  @Output() inputTapped = new EventEmitter<any>();
  @Output() appendButtonAction = new EventEmitter<boolean>();
  @Output() prependButtonAction = new EventEmitter<boolean>();

  disabled: boolean;
  timeoutId: any = null;
  onSubmitError = false;
  showPassword = false;
  isFocussed = false;
  _readonly = false;
  appendButtonConfig: ButtonConfig;
  prependButtonConfig: ButtonConfig;

  constructor(
    @Self() @Optional() public ngControl: NgControl,
    private xdaStringsService: XdaStringsService,
    private renderer: Renderer2,
    private form: NgForm,
  ) {
    this.ngControl.valueAccessor = this;
  }

  ngOnInit(): void {
    this.setupFields();

    const control = this.ngControl.control;
    const validators: ValidatorFn[] = control?.validator
      ? [control.validator]
      : [];
    if (this.required) {
      validators.push(Validators.required);
    }
    if (this.pattern) {
      validators.push(Validators.pattern(this.pattern));
    }
    if (this.type === 'email') {
      validators.push(Validators.email);
    }
    if (this.compareto) {
      validators.push(this.compareValidator);
    }
    control?.valueChanges.subscribe(() => this.valueChanges());
    control?.setValidators(validators);
    control?.updateValueAndValidity();

    // save original value of readonly to _readonly before changing it on MFD
    this._readonly = this.readonly;

    // if isMFD set readonly to true. This prevents the keyboard on Versa from have extra 'tab' keys at the top of the keyboard
    if (this.isMfd) {
      this.readonly = true;
    }
  }

  ngAfterViewInit() {
    if (this.classes) {
      const arr = this.classes.split(' ');
      arr.forEach((c) => {
        this.renderer.addClass(this.input.nativeElement, c);
        if (this.buttonPrepend) {
          this.renderer.addClass(this.buttonPrepend.nativeElement, c);
        }
        if (this.buttonAppend) {
          this.renderer.addClass(this.buttonAppend.nativeElement, c);
        }
      });
    }
    if (this.autofocus) {
      this.focus();
    }

    const ngModel = this.ngControl as NgModel;
    if (ngModel.viewModel) {
      setTimeout(() => {
        const control = this.ngControl.control;
        control?.markAsDirty();
        control?.markAsTouched();
        control?.updateValueAndValidity();
      }, 1);
    }
  }

  setupFields() {
    if (this.placeholder) {
      const text = this.xdaStringsService.strings[this.placeholder];
      if (text) {
        this.placeholder = text;
      }
    } else {
      this.placeholder = '';
    }

    if (this.type === 'password' && this.patternerror && !this.pattern) {
      this.pattern =
        '((?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])|(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%&/=?_.,:;\\-])|(?=.*[a-z])(?=.*[0-9])(?=.*[!@#$%&/=?_.,:;\\-])|(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%&/=?_.,:;\\-])).{8,}';
    }

    // setup appendButton config
    if (this.appendButton) {
      this.appendButtonConfig = JSON.parse(this.appendButton);

      // translate tool tip and text
      this.appendButtonConfig.tooltip = this.appendButtonConfig.tooltip
        ? this.xdaStringsService.strings[this.appendButtonConfig.tooltip]
        : '';
      this.appendButtonConfig.text = this.appendButtonConfig.text
        ? this.xdaStringsService.strings[this.appendButtonConfig.text]
        : '';
    }

    // setup prependButton config
    if (this.prependButton) {
      this.prependButtonConfig = JSON.parse(this.prependButton);

      // translate tool tip and text
      this.prependButtonConfig.tooltip = this.prependButtonConfig.tooltip
        ? this.xdaStringsService.strings[this.prependButtonConfig.tooltip]
        : '';
      this.prependButtonConfig.text = this.prependButtonConfig.text
        ? this.xdaStringsService.strings[this.prependButtonConfig.text]
        : '';
    }
  }

  writeValue(obj: any): void {
    // setTimeout because somewhere during initialization the default value is being erased
    setTimeout(() => {
      if (obj !== undefined) {
        this.input.nativeElement.value = obj;
        // if entering text greater than max length trim and show max length error
        if (
          this.maxlength > 0 &&
          this.input.nativeElement.value.length > this.maxlength
        ) {
          this.ngControl?.control?.setValue(
            this.input.nativeElement.value.substring(0, this.maxlength),
          );
          this.showMaxLengthError();
        }
      }
    });
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onChange() {}

  onTouched() {}

  onKeyPress(event: KeyboardEvent) {
    if (
      this.type === 'number' &&
      !(!isNaN(parseFloat(event.key)) && isFinite(+event.key))
    ) {
      return false;
    }
    // Handle if user tries to enter text past max length
    if (
      this.maxlength > 0 &&
      this.input.nativeElement.value.length == this.maxlength &&
      !event.ctrlKey
    ) {
      this.showMaxLengthError();
      return false; // ignore keys beyond max length
    }
    return;
  }

  onClick(event: Event) {
    this.inputClicked.emit(event);
  }

  onTap(event: Event) {
    this.inputTapped.emit(event);
  }

  showMaxLengthError() {
    this.ngControl?.control?.setErrors({ maxlength: true });
    this.ngControl?.control?.markAsDirty();
    this.ngControl?.control?.markAsTouched();
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId);
    }
    this.timeoutId = setTimeout(() => {
      if (
        this.ngControl?.control?.hasError('maxlength') &&
        this.ngControl?.control?.errors
      ) {
        delete this.ngControl.control.errors['maxlength'];
        this.ngControl.control.markAsPristine();
        this.ngControl.control.updateValueAndValidity();
        this.timeoutId = null;
      }
    }, 3000);
  }

  onFocus() {
    this.isFocussed = true;

    // if isMfd and if the original value for readonly wasn't false enable the input for text entry when the input is brought into focus
    if (this.isMfd && !this._readonly) {
      this.readonly = false;
    }

    // trigger inputFocused event
    this.inputFocused.emit(this.input.nativeElement);
  }

  onBlur() {
    this.isFocussed = false;

    // trigger inputBlurred event
    this.inputBlurred.emit();

    // if isMfd and if the original value for readonly wasn't false make input readonly when the input loses focus
    if (this.isMfd && !this._readonly) {
      this.readonly = true;
    }
  }

  valueChanges() {
    this.clearCustomError();
    this.isValid.emit(!this.ngControl?.control?.invalid);
  }

  clearCustomError() {
    this.error = '';
    this.noValidateError = '';
    this.onSubmitError = false;
  }

  togglePassword() {
    if (!this.disabled) {
      this.showPassword = !this.showPassword;
      if (this.showPassword) {
        this.type = 'text';
      } else {
        this.type = 'password';
      }
    }
  }

  compareValidator: ValidatorFn = (
    control: AbstractControl,
  ): ValidationErrors | null => {
    const field1 = control.value;
    let field2 = '';
    let field2Control: AbstractControl | null = null;
    if (control.parent) {
      field2 = control?.parent?.get(this.compareto)?.value;
      field2Control = control?.parent?.get(this.compareto);
      field2Control?.valueChanges.subscribe(() => {
        if (field2Control) this.watchField(field2Control);
      });
    }
    return field1 !== field2 &&
      field2Control &&
      field2Control.touched &&
      field2Control.value
      ? { match: true }
      : null;
  };

  watchField(control: AbstractControl) {
    if (
      this.ngControl?.control?.value &&
      this.ngControl?.control?.touched &&
      this.ngControl?.control?.value !== control.value
    ) {
      this.ngControl?.control?.setErrors({ match: true });
    } else {
      if (
        this.ngControl?.control?.errors &&
        this.ngControl?.control?.errors['match']
      ) {
        delete this.ngControl?.control?.errors['match'];
        this.ngControl?.control?.updateValueAndValidity();
      }
    }
  }

  registerOnValidatorChange(fn: () => void): void {
    throw new Error('Method not implemented.');
  }

  blur() {
    this.input.nativeElement.blur();
  }

  focus() {
    this.input.nativeElement.focus();
  }

  onPrependButtonAction(e: boolean) {
    this.appendButtonAction.emit(e);
  }
  onAppendButtonAction(e: boolean) {
    this.appendButtonAction.emit(e);
  }

  isErrorState(): boolean {
    return this.ngControl?.control?.invalid &&
      this.ngControl?.control?.dirty &&
      !this.ngControl?.control?.untouched
      ? true
      : false;
  }

  hasAnyError(): boolean {
    if (this.ngControl?.control?.errors) {
      return (
        this.ngControl?.control.errors['required'] ||
        (this.ngControl.control.errors['pattern'] && this.patternerror) ||
        (this.ngControl.control.errors['minlength'] && this.minlengtherror) ||
        this.ngControl.control.errors['maxlength'] ||
        this.ngControl.control.errors['email'] ||
        this.ngControl.control.errors['match'] ||
        this.ngControl.control.errors['customerror']
      );
    }
    return false;
  }

  onSubmit($event: Event) {
    if (this.form.submitted && this.ngControl?.control?.invalid) {
      this.onSubmitError = true;
    }
  }
}

interface ButtonConfig {
  type: string;
  class: string;
  tooltip: string;
  tooltipTrigger: string;
  text?: string;
}
